/**
 * Router Analytics Middleware
 * Auto-tracks route changes for TanStack Router
 */

import type { AnyRouter } from '@ufc/core'
import { analytics } from '../service'

/**
 * Options for router analytics
 */
export interface RouterAnalyticsOptions {
  /** Enable debug logging */
  debug?: boolean
  /** Custom page title extractor */
  getTitleFromMatch?: (match: unknown) => string | undefined
}

/**
 * Create router analytics middleware
 * Subscribes to router events and tracks page views
 */
export function createRouterAnalyticsMiddleware(
  router: AnyRouter,
  options: RouterAnalyticsOptions = {}
): () => void {
  const { debug = false, getTitleFromMatch } = options

  let lastPath: string | null = null
  let pageViewStartTime = Date.now()

  // Subscribe to route changes
  const unsubscribe = router.subscribe('onResolved', (event) => {
    const { toLocation } = event

    // Skip if same path (query/hash changes only)
    if (lastPath === toLocation.pathname) {
      return
    }

    // Calculate time spent on previous page
    const previousPageDuration = Date.now() - pageViewStartTime
    pageViewStartTime = Date.now()

    // Get route info
    const matches = router.state.matches
    const currentMatch = matches[matches.length - 1]

    // Get title
    let title = document.title
    if (getTitleFromMatch && currentMatch) {
      const customTitle = getTitleFromMatch(currentMatch)
      if (customTitle) {
        title = customTitle
      }
    }

    // Track page view
    analytics.trackPageView({
      pagePath: toLocation.pathname,
      title,
      routeName: currentMatch?.routeId,
      routeParams: currentMatch?.params as Record<string, string>,
      previousPageDuration: lastPath ? previousPageDuration : undefined,
    })

    if (debug) {
      console.log('[Router Analytics] Page view:', toLocation.pathname, {
        routeName: currentMatch?.routeId,
        previousPageDuration,
      })
    }

    lastPath = toLocation.pathname
  })

  return unsubscribe
}

/**
 * Initialize router analytics
 * Call this once after creating the router
 */
export function initRouterAnalytics(
  router: AnyRouter,
  options?: RouterAnalyticsOptions
): () => void {
  return createRouterAnalyticsMiddleware(router, options)
}
